import React, { useState, useEffect } from 'react';

// Importer dine under-komponenter
import CensorOverblik from './components/reports/CensorOverblik';
import FordelteCensorer from './components/reports/FordelteCensorer';
import IkkeFordelteCensorer from './components/reports/IkkeFordelteCensorer';
import AntalProevedeltagere from './components/reports/AntalProevedeltagere';
import SprogcentresEgneCensorer from './components/reports/SprogcentresEgneCensorer';
import KontaktpersonerOverblik from './components/reports/KontaktpersonerOverblik';

// Definer sektionerne
const UAFHÆNGIGE_RAPPORTER = [
  'Censoroverblik',
  'Kontaktpersoner'
];

const TERMIN_AFHÆNGIGE_RAPPORTER = [
  'Fordelte censorer',
  'Ikke fordelte censorer',
  'Antal prøvedeltagere pr. sprogcenter og danskprøve',
  'Sprogcentres egne beskikkede censorer',
  'Godkendelsesstatus for censoroverblik',
  'Honorarliste',
  'Bestilling af prøvepakker'
];

function DokumentGenerering() {
  // State for data
  const [terminer, setTerminer] = useState([]); // Vil indeholde [{id, navn}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for flow-kontrol
  const [valgtTerminId, setValgtTerminId] = useState('');
  const [aktivSektion, setAktivSektion] = useState(UAFHÆNGIGE_RAPPORTER[0]); // Start med den første uafhængige rapport

  // API Endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const API_BASE_URL_TERMINER = `${API_BASE_URL}/api/terminer`;

  // Hent listen af terminer ved start
  useEffect(() => {
    const fetchTerminer = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(API_BASE_URL_TERMINER, { credentials: 'include' });
        if (!response.ok) throw new Error('Kunne ikke hente terminer.');
        const data = await response.json();
        setTerminer(data);
        if (data.length > 0) {
          setValgtTerminId(data[0].id.toString());
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTerminer();
  }, [API_BASE_URL_TERMINER]); // Tilføjet afhængighed
  
  // Find det fulde navn på den valgte termin
  const valgtTerminNavn = terminer.find(t => t.id.toString() === valgtTerminId)?.navn || '';

  const handleTilbage = () => {
    setAktivSektion(null); // Gå tilbage til funktionsvælgeren
  };

  // Funktion der bygger og åbner download-URL'en
  const handleDownload = (rapportType) => {
    let url = '';

    // Byg den korrekte URL baseret på rapport-typen
    switch (rapportType) {
      // Termin-uafhængige rapporter
      case 'Censoroverblik':
        url = `${API_BASE_URL}/api/csv/censorer-alle`;
        break;
      case 'Kontaktpersoner':
        url = `${API_BASE_URL}/api/csv/kontaktpersoner-pr-sprogcenter`;
        break;

      // Termin-afhængige rapporter
      case 'Fordelte censorer':
      case 'Ikke fordelte censorer':
      case 'Antal prøvedeltagere pr. sprogcenter og danskprøve':
      case 'Sprogcentres egne beskikkede censorer':
      case 'Godkendelsesstatus for censoroverblik':
        if (!valgtTerminId) {
          alert('Vælg venligst en termin først for denne rapport.');
          return; // Stop funktionen
        }
        // Byg den korrekte sti for hver rapport
        const pathMap = {
            'Fordelte censorer': 'fordelte-censorer',
            'Ikke fordelte censorer': 'ikke-fordelte-censorer',
            'Antal prøvedeltagere pr. sprogcenter og danskprøve': 'deltagerantal',
            'Sprogcentres egne beskikkede censorer': 'beskikkede-censorer-pr-sprogcenter',
            'Godkendelsesstatus for censoroverblik': 'censoroverblik-godkendelse'
        };
        url = `${API_BASE_URL}/api/csv/${pathMap[rapportType]}/${valgtTerminId}`;
        break;

      case 'Censorer fra andre sprogcentre (ZIP)':
        if (!valgtTerminId) {
          alert('Vælg venligst en termin først.');
          return;
        }
        url = `${API_BASE_URL}/api/csv/zip/censorer-fra-andre-sprogcentre/${valgtTerminId}`;
        break;

      default:
        alert(`Funktionalitet for "${rapportType}" er endnu ikke implementeret.`);
        return;
    }

    // Åbn URL'en for at starte download
    window.open(url, '_blank');
  };

  return (
    <div>
      <h2 className="page-main-header">Dokumentgenerering</h2>
      {error && <p className="error-message">{error}</p>}
      
      {/* --- SEKTION FOR TERMIN-UAFHÆNGIGE RAPPORTER --- */}
      <div className="report-group-container">
        <h3>Generelle Rapporter</h3>
        <div className="function-selector-grid">
            <button className="function-card-button" onClick={() => handleDownload('Censoroverblik')}>
              <h4>Censoroverblik</h4>
            </button>
            <button className="function-card-button" onClick={() => handleDownload('Kontaktpersoner')}>
              <h4>Kontaktpersoner</h4>
            </button>
        </div>
      </div>

      {/* --- SEKTION FOR TERMIN-AFHÆNGIGE RAPPORTER --- */}
      <div className="report-group-container">
        <h3>Termin-specifikke Rapporter</h3>
        <div className="termin-selector-container">
          <label htmlFor="terminSelector">Vælg Aktiv Termin</label>
          {loading ? ( <p>Henter terminer...</p> ) : (
            <select id="terminSelector" value={valgtTerminId} onChange={(e) => setValgtTerminId(e.target.value)}>
              <option value="">-- Vælg en termin --</option>
              {terminer.map(t => (<option key={t.id} value={t.id}>{t.navn}</option>))}
            </select>
          )}
          {!valgtTerminId && !loading && <small style={{display:'block', marginTop:'5px', color:'red'}}>Vælg venligst en termin for at se muligheder.</small>}
        </div>

        <div className="function-selector-grid">
            <button className="function-card-button" onClick={() => handleDownload('Fordelte censorer')} disabled={!valgtTerminId} title={!valgtTerminId ? 'Vælg en termin først' : ''}>
              <h4>Fordelte censorer</h4>
            </button>
            <button className="function-card-button" onClick={() => handleDownload('Ikke fordelte censorer')} disabled={!valgtTerminId} title={!valgtTerminId ? 'Vælg en termin først' : ''}>
              <h4>Ikke fordelte censorer</h4>
            </button>
             <button className="function-card-button" onClick={() => handleDownload('Antal prøvedeltagere pr. sprogcenter og danskprøve')} disabled={!valgtTerminId} title={!valgtTerminId ? 'Vælg en termin først' : ''}>
              <h4>Antal prøvedeltagere pr. sprogcenter og danskprøve</h4>
            </button>
             <button className="function-card-button" onClick={() => handleDownload('Sprogcentres egne beskikkede censorer')} disabled={!valgtTerminId} title={!valgtTerminId ? 'Vælg en termin først' : ''}>
              <h4>Sprogcentres egne beskikkede censorer</h4>
            </button>
            <button className="function-card-button" onClick={() => handleDownload('Godkendelsesstatus for censoroverblik')} disabled={!valgtTerminId} title={!valgtTerminId ? 'Vælg en termin først' : ''}>
              <h4>Godkendelsesstatus for censoroverblik</h4>
            </button>
            <button 
              className="function-card-button" 
              onClick={() => handleDownload('Censorer fra andre sprogcentre (ZIP)')} 
              disabled={!valgtTerminId} 
              title={!valgtTerminId ? 'Vælg en termin først' : ''}
            >
              <h4>Censorer fra andre sprogcentre</h4>
            </button>
        </div>
      </div>
    </div>
  );
}

export default DokumentGenerering;
